import { useEffect, useRef, useState } from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';

function useCounter(target: number, duration: number, active: boolean, decimals = 0) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!active) return;
    let current = 0;
    const increment = target / (duration / 16);
    const timer = setInterval(() => {
      current += increment;
      if (current >= target) { setCount(target); clearInterval(timer); }
      else setCount(parseFloat(current.toFixed(decimals)));
    }, 16);
    return () => clearInterval(timer);
  }, [active, target, duration, decimals]);
  return count;
}

function BulletCard({ text, index, isVisible }: { text: string; index: number; isVisible: boolean }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      className="flex items-center gap-3 rounded-xl px-4 py-3 cursor-default"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? (hovered ? 'translateY(-4px)' : 'translateY(0px)') : 'translateY(20px)',
        transition: `opacity 0.65s cubic-bezier(0.22,1,0.36,1) ${0.32 + index * 0.1}s, transform ${hovered ? '0.2s' : '0.4s'} cubic-bezier(0.22,1,0.36,1)`,
        background: hovered ? 'rgba(196,168,130,0.10)' : 'rgba(196,168,130,0.04)',
        border: `1px solid ${hovered ? 'rgba(196,168,130,0.28)' : 'rgba(196,168,130,0.1)'}`,
        boxShadow: hovered ? '0 0 20px rgba(196,168,130,0.08), inset 0 1px 0 rgba(196,168,130,0.1)' : 'none',
      }}
    >
      <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-200"
        style={{
          background: hovered ? 'rgba(196,168,130,0.18)' : 'rgba(196,168,130,0.1)',
          border: `1px solid ${hovered ? 'rgba(196,168,130,0.35)' : 'rgba(196,168,130,0.2)'}`,
        }}
      >
        <i className="ri-check-line text-base transition-all duration-200" style={{ color: hovered ? '#e8d8c0' : '#c4a882' }}></i>
      </div>
      <span className="text-xs sm:text-sm transition-colors duration-200" style={{ color: hovered ? 'rgba(232,216,192,1)' : 'rgba(232,216,192,0.8)' }}>{text}</span>
    </div>
  );
}

const stats = [
  { labelEn: 'New Vehicle Models', labelFr: 'Modèles de véhicules neufs', value: 500, display: (n: number) => `${Math.floor(n)}+`, icon: 'ri-car-line', delay: 0 },
  { labelEn: 'Used Vehicle Models', labelFr: "Modèles de véhicules d'occasion", value: 3, display: (n: number) => `${n.toFixed(1)}M+`, icon: 'ri-car-washing-line', delay: 80 },
  { labelEn: 'Trim Levels, Packages & Options', labelFr: 'Niveaux de finition, forfaits et options', value: 5000, display: (n: number) => `${Math.floor(n).toLocaleString()}+`, icon: 'ri-settings-3-line', delay: 160 },
  { labelEn: 'Canadian New & Used Dealerships', labelFr: "Concessionnaires canadiens neufs et d'occasion", value: 10000, display: (n: number) => `${Math.floor(n).toLocaleString()}+`, icon: 'ri-store-2-line', delay: 240 },
  { labelEn: 'Active Rebates, Promotions & Incentives', labelFr: 'Rabais, promotions et incitatifs actifs', value: 100, display: (n: number) => `${Math.floor(n)}+`, icon: 'ri-price-tag-3-line', delay: 320 },
];

export default function MarketAuthoritySection() {
  const { lang } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const [visibleCards, setVisibleCards] = useState<boolean[]>(stats.map(() => false));
  const sectionRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);

  const c0 = useCounter(500, 1800, isVisible);
  const c1 = useCounter(3, 1800, isVisible, 1);
  const c2 = useCounter(5000, 2000, isVisible);
  const c3 = useCounter(10000, 2200, isVisible);
  const c4 = useCounter(100, 1600, isVisible);
  const counters = [c0, c1, c2, c3, c4];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const observers: IntersectionObserver[] = [];
    cardRefs.current.forEach((el, i) => {
      if (!el) return;
      const obs = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) setTimeout(() => setVisibleCards(prev => { const next = [...prev]; next[i] = true; return next; }), i * 120);
        },
        { threshold: 0.15 }
      );
      obs.observe(el);
      observers.push(obs);
    });
    return () => observers.forEach(o => o.disconnect());
  }, []);

  return (
    <section ref={sectionRef} className="relative pt-14 sm:pt-20 lg:pt-24 pb-8 sm:pb-14 overflow-hidden" style={{ background: '#06060e' }}>
      {/* BG grid */}
      <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'linear-gradient(to right, rgba(196,168,130,0.04) 1px, transparent 1px), linear-gradient(to bottom, rgba(196,168,130,0.04) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
      {/* Scan lines */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute w-px h-full" style={{ background: 'linear-gradient(to bottom, transparent, rgba(196,168,130,0.3), transparent)', animation: 'scan-v 6s ease-in-out infinite', left: '25%' }} />
        <div className="absolute w-px h-full" style={{ background: 'linear-gradient(to bottom, transparent, rgba(196,168,130,0.2), transparent)', animation: 'scan-v 6s ease-in-out infinite 3s', left: '70%' }} />
      </div>
      {/* Glow radials */}
      <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse 40% 50% at 15% 50%, rgba(196,168,130,0.06) 0%, transparent 55%), radial-gradient(ellipse 40% 50% at 85% 30%, rgba(196,168,130,0.04) 0%, transparent 50%)' }} />
      <div className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(196,168,130,0.2), transparent)' }} />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-8 lg:px-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-20 items-center">
          {/* Left column */}
          <div>
            <h2 className="text-2xl sm:text-4xl lg:text-5xl font-black text-white mb-4 sm:mb-6 leading-tight" style={{ fontFamily: 'Satoshi, Inter, sans-serif', opacity: isVisible ? 1 : 0, transform: isVisible ? 'translateY(0px)' : 'translateY(36px)', transition: 'opacity 0.75s cubic-bezier(0.22,1,0.36,1) 0.05s, transform 0.75s cubic-bezier(0.22,1,0.36,1) 0.05s', letterSpacing: '-0.02em' }}>
              {lang === 'en' ? <>We Analyze the <span style={{ color: '#c4a882' }}>Entire Vehicle Market</span></> : <>Nous analysons <span style={{ color: '#c4a882' }}>l&apos;ensemble du marché automobile</span></>}
            </h2>
            <p className="text-sm sm:text-base leading-relaxed max-w-lg mb-6 sm:mb-8" style={{ color: 'rgba(196,168,130,0.55)', opacity: isVisible ? 1 : 0, transform: isVisible ? 'translateY(0px)' : 'translateY(28px)', transition: 'opacity 0.75s cubic-bezier(0.22,1,0.36,1) 0.18s, transform 0.75s cubic-bezier(0.22,1,0.36,1) 0.18s' }}>
              {lang === 'en'
                ? 'Our platform continuously scans every available option across all makes, models, and price points. Combined with expert human analysis, we ensure you never miss the perfect vehicle for your needs.'
                : 'Notre plateforme analyse en continu toutes les options disponibles parmi toutes les marques, modèles et gammes de prix. Combinée à une analyse humaine experte, nous vous assurons de ne jamais manquer le véhicule parfait pour vos besoins.'}
            </p>
            <div className="space-y-2.5 sm:space-y-3">
              {(lang === 'en'
                ? ['Real-time market data updates', 'Comprehensive pricing analysis', 'Independent expert verification']
                : ['Mises à jour des données de marché en temps réel', 'Analyse complète des prix', 'Vérification experte indépendante']
              ).map((text, i) => (
                <BulletCard key={i} text={text} index={i} isVisible={isVisible} />
              ))}
            </div>
          </div>

          {/* Right column — stat cards */}
          <div className="flex flex-col gap-2.5 sm:gap-3">
            {stats.map((stat, i) => (
              <div key={i} ref={el => { cardRefs.current[i] = el; }} className="relative rounded-xl sm:rounded-2xl border p-3 sm:p-4 flex items-center gap-3 cursor-default min-w-0 transition-all duration-300 hover:-translate-y-0.5" style={{ opacity: visibleCards[i] ? 1 : 0, transform: visibleCards[i] ? 'translateY(0px)' : 'translateY(28px)', transition: 'opacity 0.6s ease, transform 0.6s cubic-bezier(0.22,1,0.36,1)', background: 'rgba(196,168,130,0.05)', borderColor: 'rgba(196,168,130,0.15)', boxShadow: '0 0 24px rgba(196,168,130,0.05), inset 0 1px 0 rgba(196,168,130,0.08)' }}>
                {/* Pulse dot */}
                <div className="absolute top-2 right-2 w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full animate-pulse" style={{ background: '#c4a882', boxShadow: '0 0 8px 2px rgba(196,168,130,0.3)' }}></div>
                {/* Icon */}
                <div className="w-9 h-9 sm:w-11 sm:h-11 rounded-lg sm:rounded-xl flex items-center justify-center shrink-0" style={{ background: 'rgba(196,168,130,0.08)', border: '1px solid rgba(196,168,130,0.18)' }}>
                  <i className={`${stat.icon} text-base sm:text-xl text-[#c4a882]`}></i>
                </div>
                {/* Text */}
                <div className="flex-1 min-w-0">
                  <div className="text-base sm:text-2xl font-black leading-none mb-0.5 sm:mb-1" style={{ color: '#c4a882', fontFamily: 'Satoshi, Inter, sans-serif' }}>{stat.display(counters[i])}</div>
                  <div className="text-[10px] sm:text-[13px] leading-tight" style={{ color: 'rgba(196,168,130,0.5)' }}>{lang === 'en' ? stat.labelEn : stat.labelFr}</div>
                </div>
                {/* Animated bottom line */}
                <div className="absolute bottom-0 left-4 right-4 h-px rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-1000" style={{ background: 'linear-gradient(90deg, transparent, rgba(196,168,130,0.5), transparent)', width: isVisible ? '100%' : '0%', transitionDelay: `${stat.delay + 400}ms` }}></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes scan-v { 0% { transform: translateX(-100vw); } 100% { transform: translateX(100vw); } }
      `}</style>
    </section>
  );
}
